import { Query } from '@/_core/cqrs/types/queries';
import {
    TrelloStatusMappingModel,
    GitHubStatusMappingModel,
    TrelloExportPostion,
    JiraProjectModel,
    JiraProjectsWithAuthenticatedAccountModel,
} from '../commands';

export class GetTrelloListsForStorymap extends Query<GetTrelloListsForStorymapResult> {
    constructor(public StorymapId: string) {
        super('GetTrelloListsForStorymap');
    }
}

export class TrelloSettingsQuery extends Query<TrelloSettings> {
    constructor(public StorymapId: string) {
        super('TrelloSettingsQuery');
    }
}

export class GithubSettingsQuery extends Query<GithubSettings> {
    constructor(public StorymapId: string) {
        super('GithubSettingsQuery');
    }
}

export enum AttachmentSyncRunKind {
    Save = 1,
    Enable = 2,
    WizardSave = 3,
}

export enum AttachmentSyncPullMode {
    None = 0,
    /** GitHub: the pull runs as RunGitHubPullStep commands after the apply. */
    Stepped = 1,
    /** Jira: the pull runs inside the save / enable request. */
    InApply = 2,
}

export enum AttachmentRefreshGateClosedReason {
    SynchronizationDisabled = 1,
    DescriptionSyncOff = 2,
}

export enum GitHubProviderStatus {
    Ok = 0,
    ExpiredToken = 1,
    Unavailable = 2,
}

export enum JiraProviderStatus {
    Ok = 0,
    ExpiredToken = 1,
    Unavailable = 2,
    WorkspaceDisabled = 3,
}

/** Jira structure-sync levels proposed by a settings save or wizard save; null = the stored settings. */
export interface AttachmentSyncJiraStructure {
    IsStructureSyncEnabled: boolean;
    ActivityLevelExportingItemTypeId: string | null;
    TaskLevelExportingItemTypeId: string | null;
}

/**
 * What a settings save, synchronization enable or wizard first save of the map's linked tracker (GitHub or Jira)
 * will run; nothing is saved.
 */
export class AttachmentSyncPreview extends Query<AttachmentSyncPreviewResult> {
    constructor(
        public StorymapId: string,
        public Kind: AttachmentSyncRunKind,
        public AttachmentAccessEnabled: boolean | null,
        public SynchronizeDescriptionChanges: boolean,
        public StatusMappings: GitHubStatusMappingModel[] | null,
        public JiraStructure: AttachmentSyncJiraStructure | null = null
    ) {
        super('AttachmentSyncPreview');
    }
}

export interface AttachmentSyncPreviewResult {
    WillPull: boolean;
    PullMode?: AttachmentSyncPullMode;
    PullCardIds: string[];
    PullReleaseIds: string[];
    WillOfferRefresh: boolean;
    RefreshCardIds: string[];
    EstimateSeconds: number;
    GatesClosedReason: AttachmentRefreshGateClosedReason | null;
}

export class JiraSettingsQuery extends Query<JiraSettings> {
    constructor(public StorymapId: string) {
        super('JiraSettingsQuery');
    }
}

export class GetJiraExportFieldsForIssueType extends Query<JiraExportField[]> {
    constructor(public StorymapId: string, public IssueTypeId: string) {
        super('GetJiraExportFieldsForIssueType');
    }
}

export class GetJiraProjects extends Query<JiraProjectModel[]> {
    constructor(public StorymapId: string) {
        super('GetJiraProjects');
    }
}

export class GetJiraProjectsWithAuthenticatedAccount extends Query<JiraProjectsWithAuthenticatedAccountModel> {
    constructor(public StorymapId: string) {
        super('GetJiraProjectsWithAuthenticatedAccount');
    }
}

export interface GetTrelloListsForStorymapResult {
    Lists: TrelloListModel[];
}

export interface TrelloListModel {
    Id: string;
    Name: string;
}

export interface TrelloSettings {
    IsEnabled: boolean;
    BoardName: string;
    IsTitleSyncEnabled: boolean;
    IsDescriptionSyncEnabled: boolean;
    ExportListId: string;
    ExportPosition: TrelloExportPostion;
    IsCardLinkEnabled: boolean;
    IsImportEnabledForEditors: boolean;
    IgnoreArchivedListOnImport: boolean;
    IntegrationIdentifier: string;
    HasExpiredToken: boolean;

    Lists: TrelloListModel[];
    StatusMappings: TrelloStatusMappingModel[];
}

export interface GithubSettings {
    IsEnabled: boolean;
    BoardName: string;
    IsTitleSyncEnabled: boolean;
    IsDescriptionSyncEnabled: boolean;
    IsReleaseSyncEnabled: boolean;
    ExportLabel: string;
    IsImportEnabledForEditors: boolean;
    ImportLabelFilter: string;
    HasExpiredToken: boolean;
    IsAttachmentAccessEnabled: boolean;
    ProviderStatus: GitHubProviderStatus;

    StatusMappings: GitHubStatusMappingModel[];
}

export interface JiraSettings {
    IsEnabled: boolean;

    ProjectName: string;
    IsTitleSyncEnabled: boolean;
    IsDescriptionSyncEnabled: boolean;
    IsEstimationSyncEnabled: boolean;
    IsReleaseSyncEnabled: boolean;
    CanSyncReleases: boolean;
    IsImpersonationEnabled: boolean;
    HasExpiredToken: boolean;

    ShouldAddStoriesOnBoardLink: boolean;
    ExportingIssueTypeId: string;
    IssueTypes: JiraIssueTypeModel[];
    CustomExportValues: JiraCustomExportValueModel[];
    AllExportFields: JiraExportField[];
    StatusMapping: StatusMappingModel[];
    ImportingIssueTypeIds: string[];

    IsJqlEnabled: boolean;
    Jql: string;
    IsImportEnabledForEditors: boolean;
    ShouldIgnoreClosedIssuesOnImport: boolean;
    IsStructureSyncEnabled: boolean;
    RootUrl: string;
    ActivityLevelExportingItemTypeId: string;
    TaskLevelExportingItemTypeId: string;
    IssueTypeAbbreviations: JiraIssueTypeAbbreviationModel[];
    IsAttachmentAccessEnabled?: boolean;
    ProviderStatus?: JiraProviderStatus;
}

export interface JiraIssueTypeAbbreviationModel {
    IssueTypeId: string;
    Abbreviation: string;
}

export interface StatusMappingModel {
    IssueTypeId: string;
    StatusId: string;
    StoriesOnBoardStatusKey: number;
}

export interface JiraCustomExportValueModel {
    FieldKey: string;
    Value: string[];
}

export interface JiraExportField {
    Id: string;
    Name: string;
    Required: boolean;
    Type: JiraExportFieldType;
    IsSupported: boolean;
    PossibleValues: JiraExportFieldValue[];
}

export enum JiraExportFieldType {
    Undefined = 0,
    String = 1,
    Select = 2,
    MultiSelect = 3,
}

export interface JiraExportFieldValue {
    Key: string;
    Value: string;
}

export interface JiraIssueTypeModel {
    Id: string;
    Name: string;
    StoryPointFieldId: string;
    Statuses: JiraStatusModel[];
    IsEpic: boolean;
    EpicNameFieldId: string;
    EpicLinkFieldId: string;
    HasReleaseField: boolean;
}

export interface JiraStatusModel {
    Id: string;
    Name: string;
}
